import React, { useState } from 'react';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { Avatar, Button, CircularProgress, Container, CssBaseline, FormControlLabel, Grid, TextField, Typography, Autocomplete, Chip, Switch} from "@mui/material";
import clsx from 'clsx';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import HeaderMenu from "../../components/header-menu";
import CustomizedSnackbars from "../../components/material-snackbars/index.js";
import { browserHistory } from '../../helpers/history';
import Api from "../../services/api";
import { Overlay } from "../../styles/global.js";
import { Styles } from '../../styles/material-styles';
import { withStyles } from '@mui/styles';
import { NotificationVariant } from 'src/utils/enums/notification-variants.enum';
import * as LLMEventService from "../../services/llmevent.service";
import { LLM_EVENT_SOURCE, getLlmEventSourceOptions } from 'src/utils/enums/llm-event-source.enum';

const itemInitialState = {
  name: '',
  system: false,
  // Origem do match (1=UserMessage, 2=LlmResponse). Default = mensagem do usuario
  // — comportamento historico. Ver LLM_EVENT_SOURCE pra semantica completa.
  source: LLM_EVENT_SOURCE.USER_MESSAGE,
  // So faz sentido quando source=LlmResponse. Toggle no UI so aparece nesse caso.
  suppressLlmMessage: false,
};

const LLMEventRegistration = (props) => {
  const { classes, intl } = props;

  const history = browserHistory;
  
  const { headerMenu, actionsHeaderMenu, userSession } = useSelector(state => ({
    actionsHeaderMenu: state.actionsHeaderMenu,
    headerMenu: state.headerMenu,
    userSession: state.userSession,
  }));
  
  const [loading, setLoading] = useState(false);
  const [openNotification, setOpenNotification] = useState(false);
  const [notificationVariant, setNotificationVariant] = useState('error');
  const [notificationMessage, setNotificationMessage] = useState('');

  const [item, setItem] = useState(itemInitialState);
  const [keyWords, setKeyWords] = useState([]);
  
  if (userSession == null || userSession.user == null || userSession.user.userrole == null) {
    Api.logoff();
  }

  if (Api == null) {
    const language = props.match.params.lang;
    document.location.href=`/${language}/unavailable`;
  }
  
  const closeNotification = () => {
    setOpenNotification(false);
  };

  const showNotification = (message, variant = NotificationVariant.SUCCESS) => {
    setLoading(false);
    setOpenNotification(true);
    setNotificationVariant(variant);
    setNotificationMessage(message);
  };

  const handleInputChange = (e) => {
      const { name, value, checked } = e.target;
      let newValue = value;
      
      if (name === "system") {
        newValue = checked;
      }
  
      setItem(prev => ({
          ...prev,
          [name]: newValue
      }));
  };

  const loadInitialStateComponent = () => {
    setItem(itemInitialState);
    setKeyWords([]);
  }

  const handleSubmit = async (event) => {
    setLoading(true);
    event.preventDefault();

    try {
      // suppressLlmMessage soh faz sentido em LlmResponse. Se o admin
      // trocou a origem no meio da edicao, garantimos que UserMessage
      // envia sempre false — nao poluir o BD com semantica invalida.
      const normalizedItem = {
        ...item,
        suppressLlmMessage: item.source === LLM_EVENT_SOURCE.LLM_RESPONSE
          ? !!item.suppressLlmMessage
          : false,
      };
      let payload = {...normalizedItem, keywords: keyWords};

      const response = await LLMEventService.create(payload);

      if (response?.success) {
        loadInitialStateComponent();
        showNotification(intl.formatMessage({ id: "add.success" }));
      } else {
        showNotification("Não foi possível salvar o registro.", NotificationVariant.ERROR);
      }
    } catch (err) {
      var message = err?.response?.data?.errors[0] ?? "Não foi possível salvar o registro.";
      showNotification(message, NotificationVariant.ERROR);
    } finally {
      setLoading(false);
    }
}
  
  return (
    <div className={classes.root}>
      <HeaderMenu />

      <main
        className={clsx(classes.content, {
          [classes.contentShift]: headerMenu.open,
        }, classes.actionsContent, {
          [classes.actionscontentShift]: actionsHeaderMenu.open,
        })}
      >
        <div className={classes.drawerHeader} />
        
        <Container component='main' maxWidth='md'>
          <CssBaseline />
          <div className={classes.paper} style={{ marginTop: 0 }}>
            <Grid container spacing={6}>
              <Grid item xs={12} sm={4}>
                <Button variant='outlined' color='primary' onClick={history.goBack}>
                  <FormattedMessage id="back" />
                </Button>
              </Grid>
              <Grid item xs={12} sm={8} />
            </Grid>

            <Avatar mt={7} style={{ backgroundColor: '#303f9f', color: 'white' }}>
              <SmartToyIcon />
            </Avatar>

            <Typography component='h1' variant='h5' mt={2}>
              {intl.formatMessage({ id: "llm.event.registration" })}
            </Typography>
             
            <form name='myForm' className={classes.form} onSubmit={handleSubmit} encType='multipart/form-data'>
                <Grid container spacing={2}>

                    <Grid item xs={12} sm={12}>
                        <TextField 
                            inputProps={{ maxLength: 250 }} 
                            fullWidth 
                            label={intl.formatMessage({id:"name"})} 
                            variant='outlined' 
                            name='name' 
                            onChange={handleInputChange} 
                            value={item.name} 
                            required 
                        />
                    </Grid>

                    <Grid item xs={12} sm={12}>
                        <Autocomplete
                            multiple
                            id="tags-filled"
                            options={[]}
                            freeSolo
                            value={keyWords}
                            onChange={(event, newValue, reason) => {
                                setKeyWords(newValue);
                            }}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => {
                                    const { key, ...tagProps } = getTagProps({ index });
                                    return <Chip variant="outlined" label={option} key={key} {...tagProps} />;
                                })
                            }
                            renderInput={(params) => (
                                <TextField {...params} variant="filled" label={intl.formatMessage({ id: "keywords" })} />
                            )}
                        />
                    </Grid>

                    {/* Origem do evento — decide se o match roda contra a mensagem
                        do cliente (pre-LLM) ou contra a resposta gerada pela LLM
                        (pos-LLM). Autocomplete pra manter consistencia visual com o
                        resto do form; nao eh multiSelect. */}
                    <Grid item xs={12} sm={12}>
                        <Autocomplete
                            fullWidth
                            disableClearable
                            options={getLlmEventSourceOptions(intl)}
                            getOptionLabel={(opt) => opt?.label ?? ''}
                            isOptionEqualToValue={(a, b) => a?.id === b?.id}
                            value={getLlmEventSourceOptions(intl).find(o => o.id === item.source) ?? getLlmEventSourceOptions(intl)[0]}
                            onChange={(_, newVal) => setItem(prev => ({
                                ...prev,
                                source: newVal?.id ?? LLM_EVENT_SOURCE.USER_MESSAGE,
                                // Trocar source pra UserMessage zera o suppress —
                                // esse flag soh existe pra LlmResponse.
                                suppressLlmMessage: (newVal?.id === LLM_EVENT_SOURCE.LLM_RESPONSE)
                                    ? prev.suppressLlmMessage
                                    : false,
                            }))}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label={intl.formatMessage({ id: 'llm.event.source.label' })}
                                    helperText={intl.formatMessage({ id: 'llm.event.source.help' })}
                                />
                            )}
                        />
                    </Grid>

                    {/* Suprimir mensagem da IA quando este evento disparar.
                        Renderiza SO quando source=LlmResponse — a semantica nao
                        se aplica a matches sobre a mensagem do cliente (nesse
                        caso a LLM nao chega a ser invocada). */}
                    {item.source === LLM_EVENT_SOURCE.LLM_RESPONSE && (
                        <Grid item xs={12} sm={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={!!item.suppressLlmMessage}
                                        onChange={(e) => setItem(prev => ({ ...prev, suppressLlmMessage: e.target.checked }))}
                                        color="primary"
                                    />
                                }
                                label={intl.formatMessage({ id: 'llm.event.suppress.label' })}
                            />
                            <Typography variant="caption" display="block" color="textSecondary">
                                {intl.formatMessage({ id: 'llm.event.suppress.help' })}
                            </Typography>
                        </Grid>
                    )}

                    <Grid item xs={12} sm={12} md={12} lg={12} style={{ marginTop: 15, display: 'flex', justifyContent: 'flex-end', marginTop: 15 }}>
                      <Grid container justify="flex-end" style={{ justifyContent: 'flex-end' }}>
                        <Grid item xs={12} sm={4}    >
                          <Button
                            type='submit'
                            fullWidth
                            variant='contained'
                            color='primary'
                            className={classes.submit}
                          >
                            {<FormattedMessage id="save" />}
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>

                </Grid>
              </form>   
          </div>
          
          {loading && (
            <Overlay>
              <CircularProgress color='secondary' />
            </Overlay>
          )}
          
          {openNotification && (
            <CustomizedSnackbars
              variant={notificationVariant}
              message={notificationMessage}
              isOpen={openNotification}
              toClose={closeNotification}
            />
          )}          
        </Container>
      </main>
    </div>
  );
};

export default injectIntl(withStyles(Styles)(LLMEventRegistration));